import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HasPermission } from "../../components/AccessControl/ProtectedComponent";
import { createPortal } from "react-dom";
import { useInterviews } from "../../contexts/InterviewsContext";
import { useRecruitmentDashboard } from "../../contexts/RecruitmentDashboardContext";
import Modal from "../../components/UI/Modal";
import InterviewForm from "../../components/Form/InterviewForm";
import ReusableTable from "../../components/UI/ReusableTable";
import Skeleton from "react-loading-skeleton";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import {
  FaCalendarCheck,
  FaComments,
  FaClipboardList,
  FaUserTie,
  FaTimes,
  FaEllipsisV,
} from "react-icons/fa";
import Drawer from "../../components/UI/Drawer";
import CandidateDetails from "../../components/Details/CandidateDetails";
import StatusBadge from "../../components/UI/StatusBadge";
import DeleteButton from "../../components/UI/AlertDialog";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";

const Interviews = () => {
  const { dashboardData } = useRecruitmentDashboard();
  const { roles, user, hasPermission } = useAuth();
  const isHR = hasPermission("interviews_update");
  const showAssignedTab = hasPermission("interviews_read");
  const {
    interviews,
    fetchInterviews,
    addInterview,
    updateInterview,
    loading,
    pagination,
  } = useInterviews();

  // Tab state
  const [activeTab, setActiveTab] = useState("all");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isProceedModalOpen, setIsProceedModalOpen] = useState(false);
  const [proceedCandidate, setProceedCandidate] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState("");
  const [selectedInterviewStatusFilter, setSelectedInterviewStatusFilter] =
    useState("");
  const [campaignNames, setCampaignNames] = useState([]);
  const [loadingCampaignNames, setLoadingCampaignNames] = useState(false);

  // Search API states
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPagination, setSearchPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [searchPage, setSearchPage] = useState(1);

  // Modal states for offer letter actions
  const [isOfferLetterSendModalOpen, setIsOfferLetterSendModalOpen] =
    useState(false);
  const [isAcceptOfferModalOpen, setIsAcceptOfferModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  // Form states for offer letter send modal
  const [offerLetterSendData, setOfferLetterSendData] = useState({
    interview: null,
    file: null,
  });
  const [offerLetterSendErrors, setOfferLetterSendErrors] = useState({});
  const [isSubmittingOfferLetterSend, setIsSubmittingOfferLetterSend] =
    useState(false);

  // Form states for accept offer modal
  const [acceptOfferData, setAcceptOfferData] = useState({
    interview: null,
    file: null,
  });
  const [isSubmittingAcceptOffer, setIsSubmittingAcceptOffer] = useState(false);

  // Form states for reject modal
  const [rejectOfferData, setRejectOfferData] = useState({
    interview: null,
    reason: "",
  });
  const [rejectOfferErrors, setRejectOfferErrors] = useState({});
  const [isSubmittingRejectOffer, setIsSubmittingRejectOffer] = useState(false);

  // Modal states for reschedule and schedule next round
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleInterview, setRescheduleInterview] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);

  const [isScheduleNextRoundModalOpen, setIsScheduleNextRoundModalOpen] =
    useState(false);
  const [scheduleNextRoundInterview, setScheduleNextRoundInterview] =
    useState(null);
  const [isSchedulingNextRound, setIsSchedulingNextRound] = useState(false);

  // console.log("interviews: ", interviews);

  // Fetch campaign names from API
  useEffect(() => {
    const fetchCampaignNames = async () => {
      try {
        setLoadingCampaignNames(true);
        const token = getToken();

        if (!token) {
          console.warn("No token found, cannot fetch campaign names");
          setLoadingCampaignNames(false);
          return;
        }

        const response = await axios.get(
          "https://hrmspayroll.prepstripe.com/api/campaign-names/list",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // API response structure: { success: true, campaigns: [...], message: "..." }
        if (response.data?.success && response.data?.campaigns) {
          setCampaignNames(response.data.campaigns);
        } else {
          // Fallback to direct campaigns array if structure is different
          setCampaignNames(response.data?.campaigns || []);
        }
      } catch (error) {
        console.error("Error fetching campaign names:", error);
        setNotification({
          message: `Failed to load campaign names: ${error.response?.data?.message || error.message || "Unknown error"}`,
          type: "error",
        });
        setCampaignNames([]);
      } finally {
        setLoadingCampaignNames(false);
      }
    };

    fetchCampaignNames();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setSearchPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search interviews API call
  useEffect(() => {
    const searchInterviews = async () => {
      // If no search query, no status filter, no date filter, and no campaign filter, clear results and return
      if (
        !debouncedSearchQuery.trim() &&
        !selectedInterviewStatusFilter &&
        !selectedDateFilter &&
        !selectedCampaignFilter
      ) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        const token = getToken();
        const params = {
          per_page: 15,
          page: searchPage,
        };

        // Add search parameter - required by API
        // If only status/date/campaign filter is selected, send empty string
        // If search query exists, send the query
        params.search = debouncedSearchQuery.trim() || "";

        // Add interview_status parameter if status filter is selected
        if (selectedInterviewStatusFilter) {
          params.interview_status = selectedInterviewStatusFilter;
        }

        // Add scheduled_at parameter if date filter is selected
        // Format: YYYY-MM-DD to match the date part of scheduled_at
        if (selectedDateFilter) {
          params.scheduled_at = selectedDateFilter;
        }

        // Add campaign_id parameter if campaign filter is selected
        if (selectedCampaignFilter) {
          params.campaign_id = selectedCampaignFilter;
        }

        const response = await axios.get(
          "https://hrmspayroll.prepstripe.com/api/interviews/search",
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Support multiple response shapes (data.data.data, data.data, or data as array)
        const inner = response.data?.data;
        let rawList = inner?.data;
        if (!Array.isArray(rawList))
          rawList = Array.isArray(inner) ? inner : [];
        if (!Array.isArray(rawList))
          rawList = Array.isArray(response.data) ? response.data : [];

        const normalizeSearchRow = (item) => {
          if (!item) return item;
          const interview = item.interview || item;
          const candidate = item.candidate ?? interview.candidate;
          const base = { ...interview, candidate };
          base.interview_status =
            base.interview_status ?? base.status ?? item.status;
          return base;
        };
        let transformedInterviews = rawList.map(normalizeSearchRow);

        // If status filter is selected, filter client-side as fallback
        // (in case API doesn't support interview_status parameter)
        if (selectedInterviewStatusFilter) {
          transformedInterviews = transformedInterviews.filter(
            (interview) =>
              String(interview.interview_status) ===
              String(selectedInterviewStatusFilter),
          );
        }

        // If date filter is selected, filter client-side as fallback
        // (in case API doesn't support scheduled_at parameter)
        // Match the date part of scheduled_at (YYYY-MM-DD) with selected date
        if (selectedDateFilter) {
          transformedInterviews = transformedInterviews.filter((interview) => {
            const scheduledAt =
              interview.scheduled_at || interview.interview?.scheduled_at;
            if (!scheduledAt) return false;
            const interviewDate = new Date(scheduledAt)
              .toISOString()
              .split("T")[0];
            return interviewDate === selectedDateFilter;
          });
        }

        // If campaign filter is selected, filter client-side as fallback
        // (in case API doesn't support campaign_id parameter)
        // Match by campaign_id or campaign_name
        if (selectedCampaignFilter) {
          transformedInterviews = transformedInterviews.filter((interview) => {
            // Check campaign_id from campaigns array
            const campaignId =
              interview.candidate?.campaigns?.[0]?.id ||
              interview.candidate?.campaigns?.[0]?.pivot?.campaign_id ||
              interview.candidate?.campaign_id;

            // Also check campaign_name for matching
            const campaignName = interview.candidate?.campaign_name;
            const selectedCampaign = campaignNames.find(
              (c) => String(c.id) === String(selectedCampaignFilter),
            );
            const selectedCampaignName = selectedCampaign?.campaign_name;

            return (
              String(campaignId) === String(selectedCampaignFilter) ||
              (selectedCampaignName &&
                campaignName &&
                campaignName.toLowerCase() ===
                  selectedCampaignName.toLowerCase())
            );
          });
        }

        setSearchResults(transformedInterviews);

        const searchPaginationSource =
          response.data?.data || response.data || {};
        const total = searchPaginationSource.total ?? 0;
        const perPage = searchPaginationSource.per_page ?? 15;
        const totalPages = Math.ceil(total / perPage) || 1;

        setSearchPagination({
          currentPage: searchPaginationSource.current_page ?? 1,
          totalPages: totalPages,
          totalItems: total,
          perPage: perPage,
        });
      } catch (error) {
        console.error("Error searching interviews:", error);
        setNotification({
          message: `Error searching interviews: ${error.response?.data?.message || error.message || "Search failed"}`,
          type: "error",
        });
        setSearchResults([]);
        setSearchPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          perPage: 15,
        });
      } finally {
        setIsSearching(false);
      }
    };

    searchInterviews();
  }, [
    debouncedSearchQuery,
    searchPage,
    selectedInterviewStatusFilter,
    selectedDateFilter,
    selectedCampaignFilter,
    campaignNames,
  ]);

  // Summary cards configuration
  const summaryCards = [
    {
      label: "Interview Scheduled",
      count: dashboardData.interviewScheduledCount || 0,
      icon: <FaCalendarCheck className="text-blue-500 text-2xl" />,
    },
    {
      label: "Interview Feedback Pending",
      count: dashboardData.interviewFeedbackPendingCount || 0,
      icon: <FaComments className="text-blue-500 text-2xl" />,
    },
    {
      label: "Approval Pending",
      count: dashboardData.approvalPendingCount || 0,
      icon: <FaClipboardList className="text-blue-500 text-2xl" />,
    },
    {
      label: "Offer Acceptance Pending",
      count: dashboardData.offerAcceptancePendingCount || 0,
      icon: <FaUserTie className="text-blue-500 text-2xl" />,
    },
  ];

  const openModalForAdd = () => {
    setEditingInterview(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingInterview(null);
    setIsScheduling(false);
  };

  const handleFormSubmit = async (interviewData) => {
    if (editingInterview) {
      // Update interview using PUT API
      try {
        const token = getToken();
        const payload = {
          interviewers: interviewData.interviewers || [],
          assigned_to: interviewData.assigned_to,
          scheduled_at: interviewData.scheduled_at,
          interview_type: interviewData.interview_type,
          interview_link: interviewData.interview_link || null,
          location: interviewData.location || null,
          round_type: interviewData.round_type,
        };

        await axios.put(
          `https://hrmspayroll.prepstripe.com/api/interviews/${editingInterview.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        setNotification({
          message: "Interview updated successfully.",
          type: "success",
        });

        // Refresh the interviews list
        fetchInterviews(currentPage);
        closeModal();
      } catch (error) {
        // Handle validation errors
        const errorData = error.response?.data;
        let errorMessage =
          errorData?.message || error.message || "Error updating interview";

        if (errorData?.errors) {
          // Format validation errors
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, " ").replace(/\./g, " ");
              return `${fieldName}: ${Array.isArray(messages) ? messages.join(", ") : messages}`;
            })
            .join("; ");
          errorMessage = errorMessages || errorMessage;
        }

        setNotification({
          message: errorMessage,
          type: "error",
        });
      }
    } else {
      // Add new interview
      setIsScheduling(true);
      try {
        const response = await addInterview(interviewData);
        // Check if response has error or if it's a successful response
        if (
          response &&
          !response.error &&
          (response.interview || response.candidate)
        ) {
          setNotification({
            message: "Interview scheduled successfully.",
            type: "success",
          });

          // Wait a bit to ensure the API has processed the new interview
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Refresh the interviews list after success - call the API as requested
          // Call fetchInterviews to refresh the data from the API
          await fetchInterviews(currentPage);

          // Close modal only on success
          closeModal();
        } else {
          setNotification({
            message:
              response?.message ||
              response?.error ||
              "Getting issues scheduling interview",
            type: "error",
          });
        }
      } catch (error) {
        // Handle validation errors
        const errorData = error.response?.data;
        let errorMessage =
          errorData?.message ||
          error.message ||
          "Getting issues scheduling interview";

        if (errorData?.errors) {
          // Format validation errors
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, " ").replace(/\./g, " ");
              return `${fieldName}: ${Array.isArray(messages) ? messages.join(", ") : messages}`;
            })
            .join("; ");
          errorMessage = errorMessages || errorMessage;
        }

        setNotification({
          message: errorMessage,
          type: "error",
        });
      } finally {
        setIsScheduling(false);
      }
    }
  };

  const handleCloseProceedModal = () => {
    setIsProceedModalOpen(false);
    setProceedCandidate(null);
    if (editingInterview) {
      const selectElement = document.getElementById(
        `action-select-${editingInterview.id}`,
      );
      if (selectElement) {
        selectElement.value = "";
      }
    }
    setEditingInterview(null);
  };

  const openDetailsDrawer = (interview) => {
    setSelectedInterview(interview);
    setIsDrawerOpen(true);
  };

  const closeDetailsDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedInterview(null);
  };

  // Function to update candidate status with feedback
  const handleStatusUpdate = async (feedbackData) => {
    if (!selectedInterview || !selectedInterview.candidate) return;

    // feedbackData contains: { status: "Selected"/"Rejected"/"Hold", overallFeedback: "..." }
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("id", selectedInterview.id);
      formData.append("status", feedbackData.status);
      formData.append("feedback", feedbackData.overallFeedback);

      // Update candidate status via API
      await axios.post(
        "https://hrmspayroll.prepstripe.com/api/interviews/feedback",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setNotification({
        message: "Candidate status updated successfully.",
        type: "success",
      });

      // Refresh the interviews list
      fetchInterviews(currentPage);
      closeDetailsDrawer();
    } catch (error) {
      setNotification({
        message: `Error updating candidate status: ${error.response?.data?.message || error.message || error}`,
        type: "error",
      });
    }
  };

  // Function to handle edit interview
  const handleEdit = useCallback((interview) => {
    // Format interview data for the form
    const formattedInterview = {
      ...interview,
      // Convert scheduled_at from API format to datetime-local format
      scheduled_at: interview.scheduled_at
        ? new Date(interview.scheduled_at).toISOString().slice(0, 16)
        : "",
      // Use interviewers if available (full objects), otherwise convert interviewer_ids to objects
      interviewers:
        interview.interviewers && interview.interviewers.length > 0
          ? interview.interviewers
          : interview.interviewer_ids
            ? interview.interviewer_ids.map((id) => ({ employee_id: id }))
            : [],
      // Include assigned_to if it exists
      assigned_to: interview.assigned_to || "",
    };
    setEditingInterview(formattedInterview);
    setIsModalOpen(true);
  }, []);

  // Function to handle delete interview
  const handleDelete = useCallback(
    async (interviewId) => {
      try {
        const token = getToken();
        await axios.delete(
          `https://hrmspayroll.prepstripe.com/api/interviews/${interviewId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setNotification({
          message: "Interview deleted successfully.",
          type: "success",
        });

        // Refresh the interviews list
        fetchInterviews(currentPage);
      } catch (error) {
        setNotification({
          message: `Error deleting interview: ${error.response?.data?.message || error.message || error}`,
          type: "error",
        });
      }
    },
    [fetchInterviews, currentPage],
  );

  // Function to handle offer letter send - open modal
  const handleOfferLetterSend = useCallback((interview) => {
    if (!interview?.candidate) return;

    setOfferLetterSendData({
      interview: interview,
      file: null,
    });
    setOfferLetterSendErrors({});
    setIsOfferLetterSendModalOpen(true);
  }, []);

  // Function to handle offer letter send form submit
  const handleOfferLetterSendSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const errors = {};
      const interview = offerLetterSendData.interview;

      if (!interview?.candidate?.id) {
        setNotification({
          message: "Candidate ID not found.",
          type: "error",
        });
        return;
      }

      if (!offerLetterSendData.file) {
        errors.file = "Please upload a PDF document.";
        setOfferLetterSendErrors(errors);
        return;
      }

      // Validate file type
      if (offerLetterSendData.file.type !== "application/pdf") {
        errors.file = "Only PDF files are allowed.";
        setOfferLetterSendErrors(errors);
        return;
      }

      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (offerLetterSendData.file.size > maxSize) {
        errors.file = "File size must be less than 5MB.";
        setOfferLetterSendErrors(errors);
        return;
      }

      setIsSubmittingOfferLetterSend(true);
      setOfferLetterSendErrors({});

      try {
        const token = getToken();
        const formData = new FormData();
        formData.append("document", offerLetterSendData.file);
        formData.append("candidate_id", interview.candidate.id);

        // Add campaign_id to payload from campaigns array
        const campaignId =
          interview.candidate?.campaigns?.[0]?.id ||
          interview.candidate?.campaigns?.[0]?.pivot?.campaign_id;
        if (campaignId) {
          formData.append("campaign_id", campaignId);
        }

        await axios.post(
          `https://hrmspayroll.prepstripe.com/api/candidates/${interview.candidate.id}/upload-offer-document`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        setNotification({
          message: "Offer letter document uploaded successfully.",
          type: "success",
        });

        setIsOfferLetterSendModalOpen(false);
        setOfferLetterSendData({ interview: null, file: null });

        // Refresh the interviews list
        fetchInterviews(currentPage);
      } catch (error) {
        setNotification({
          message: `Error uploading offer letter: ${error.response?.data?.message || error.message || "Failed to upload document"}`,
          type: "error",
        });
      } finally {
        setIsSubmittingOfferLetterSend(false);
      }
    },
    [offerLetterSendData, fetchInterviews, currentPage],
  );

  // Function to handle accept offer letter - open modal
  const handleAcceptOfferLetter = useCallback((interview) => {
    if (!interview?.candidate) return;

    setAcceptOfferData({
      interview: interview,
      file: null,
    });
    setIsAcceptOfferModalOpen(true);
  }, []);

  // Function to handle accept offer form submit
  const handleAcceptOfferSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const interview = acceptOfferData.interview;

      if (!interview?.candidate?.id) {
        setNotification({
          message: "Candidate ID not found.",
          type: "error",
        });
        return;
      }

      if (!acceptOfferData.file) {
        setNotification({
          message: "Please upload a signed offer letter document.",
          type: "error",
        });
        return;
      }

      // Validate file type
      if (acceptOfferData.file.type !== "application/pdf") {
        setNotification({
          message: "Only PDF files are allowed.",
          type: "error",
        });
        return;
      }

      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (acceptOfferData.file.size > maxSize) {
        setNotification({
          message: "File size must be less than 5MB.",
          type: "error",
        });
        return;
      }

      // Get campaign_id from campaigns array
      const campaignId =
        interview.candidate?.campaigns?.[0]?.id ||
        interview.candidate?.campaigns?.[0]?.pivot?.campaign_id;
      if (!campaignId) {
        setNotification({
          message: "Campaign ID not found for this candidate.",
          type: "error",
        });
        return;
      }

      setIsSubmittingAcceptOffer(true);

      try {
        const token = getToken();
        const formData = new FormData();
        formData.append("signed_document", acceptOfferData.file);
        formData.append("candidate_id", interview.candidate.id);
        formData.append("campaign_id", campaignId);

        await axios.post(
          `https://hrmspayroll.prepstripe.com/api/candidates/${interview.candidate.id}/upload-signed-offer-document`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        setNotification({
          message: "Signed offer letter document uploaded successfully.",
          type: "success",
        });

        setIsAcceptOfferModalOpen(false);
        setAcceptOfferData({ interview: null, file: null });

        // Refresh the interviews list
        fetchInterviews(currentPage);
      } catch (error) {
        setNotification({
          message: `Error uploading signed offer letter: ${error.response?.data?.message || error.message || "Failed to upload document"}`,
          type: "error",
        });
      } finally {
        setIsSubmittingAcceptOffer(false);
      }
    },
    [acceptOfferData, fetchInterviews, currentPage],
  );

  // Function to handle reject offer - open modal
  const handleRejectOffer = useCallback((interview) => {
    if (!interview?.candidate) return;

    setRejectOfferData({
      interview: interview,
      reason: "",
    });
    setRejectOfferErrors({});
    setIsRejectModalOpen(true);
  }, []);

  // Function to handle reject offer form submit
  const handleRejectSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const errors = {};
      const interview = rejectOfferData.interview;

      if (!interview?.candidate?.id) {
        setNotification({
          message: "Candidate ID not found.",
          type: "error",
        });
        return;
      }

      if (!interview?.candidate?.email) {
        setNotification({
          message: "Candidate email not found.",
          type: "error",
        });
        return;
      }

      // Get campaign_id from campaigns array
      const campaignId =
        interview.candidate?.campaigns?.[0]?.id ||
        interview.candidate?.campaigns?.[0]?.pivot?.campaign_id;
      if (!campaignId) {
        setNotification({
          message: "Campaign ID not found for this candidate.",
          type: "error",
        });
        return;
      }

      // Validate reason field
      if (!rejectOfferData.reason.trim()) {
        errors.reason = "Reason is required.";
        setRejectOfferErrors(errors);
        return;
      }

      if (rejectOfferData.reason.length > 200) {
        errors.reason = "Reason must be 200 characters or less.";
        setRejectOfferErrors(errors);
        return;
      }

      if (rejectOfferData.reason.length < 10) {
        errors.reason = "Reason must be at least 10 characters.";
        setRejectOfferErrors(errors);
        return;
      }

      setIsSubmittingRejectOffer(true);
      setRejectOfferErrors({});

      try {
        const token = getToken();
        await axios.post(
          `https://hrmspayroll.prepstripe.com/api/candidates/${interview.candidate.id}/reject-offer `,
          {
            email: interview.candidate.email,
            reason: rejectOfferData.reason,
            candidate_id: interview.candidate.id,
            campaign_id: campaignId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setNotification({
          message: "Offer rejected successfully.",
          type: "success",
        });

        setIsRejectModalOpen(false);
        setRejectOfferData({ interview: null, reason: "" });

        // Refresh the interviews list
        fetchInterviews(currentPage);
      } catch (error) {
        setNotification({
          message: `Error rejecting offer: ${error.response?.data?.message || error.message || "Failed to reject offer"}`,
          type: "error",
        });
      } finally {
        setIsSubmittingRejectOffer(false);
      }
    },
    [rejectOfferData, fetchInterviews, currentPage],
  );

  // Function to handle reschedule round - open modal
  const handleRescheduleRound = useCallback((interview) => {
    if (!interview) return;

    // Get current round data
    const currentRound =
      interview.current_round ?? interview.interview?.current_round;
    const rounds = interview.rounds || [];
    const matchingRound = rounds.find(
      (round) => round.round_number === currentRound,
    );

    if (!matchingRound) {
      setNotification({
        message: "Current round not found.",
        type: "error",
      });
      return;
    }

    // Extract assigned_to employee_id - handle both object and ID formats
    let assignedToValue = "";
    if (matchingRound.assigned_to) {
      if (
        typeof matchingRound.assigned_to === "object" &&
        matchingRound.assigned_to.employee_detail
      ) {
        // Extract employee_id from nested structure
        assignedToValue =
          matchingRound.assigned_to.employee_detail.employee_id || "";
      } else if (
        typeof matchingRound.assigned_to === "object" &&
        matchingRound.assigned_to.employee_id
      ) {
        // Direct employee_id on assigned_to object
        assignedToValue = matchingRound.assigned_to.employee_id;
      } else if (
        typeof matchingRound.assigned_to === "string" ||
        typeof matchingRound.assigned_to === "number"
      ) {
        // Already an ID
        assignedToValue = String(matchingRound.assigned_to);
      }
    } else if (interview.assigned_to) {
      // Fallback to interview level assigned_to
      if (
        typeof interview.assigned_to === "object" &&
        interview.assigned_to.employee_detail
      ) {
        assignedToValue =
          interview.assigned_to.employee_detail.employee_id || "";
      } else if (
        typeof interview.assigned_to === "object" &&
        interview.assigned_to.employee_id
      ) {
        assignedToValue = interview.assigned_to.employee_id;
      } else {
        assignedToValue = String(interview.assigned_to);
      }
    }

    // Extract interviewers from assignments if available, otherwise use interviewers array
    let roundInterviewers =
      matchingRound.interviewers || interview.interviewers || [];
    if (matchingRound.assignments && matchingRound.assignments.length > 0) {
      // Map assignments to interviewer format
      roundInterviewers = matchingRound.assignments
        .map((assignment) => {
          const interviewer = assignment.interviewer;
          if (interviewer && interviewer.employee_detail) {
            return {
              id: interviewer.id,
              employee_id: interviewer.employee_detail.employee_id,
              full_name:
                interviewer.name ||
                interviewer.employee_detail.full_name ||
                `Interviewer ${interviewer.employee_detail.employee_id}`,
              name: interviewer.name,
              email: interviewer.email,
            };
          }
          return interviewer;
        })
        .filter(Boolean);
    }

    // Format interview data for the form
    const formattedData = {
      ...interview,
      // Pre-fill with current round data
      scheduled_at: matchingRound.scheduled_at
        ? new Date(matchingRound.scheduled_at).toISOString().slice(0, 16)
        : "",
      interview_type: matchingRound.interview_type || "",
      interview_link: matchingRound.interview_link || "",
      location: matchingRound.location || "",
      round_type:
        matchingRound.round_type || interview.expected_round_type || "",
      interviewers: roundInterviewers,
      assigned_to: assignedToValue,
      candidate_id: interview.candidate?.id || interview.candidate_id,
      campaign_id: interview.campaign_id || interview.candidate?.campaign_id,
      round_id: matchingRound.id, // Store round ID for API call
    };

    setRescheduleInterview(formattedData);
    setIsRescheduleModalOpen(true);
  }, []);

  // Function to handle reschedule form submit
  const handleRescheduleSubmit = useCallback(
    async (interviewData) => {
      if (!rescheduleInterview?.round_id) {
        setNotification({
          message: "Round ID not found. Cannot reschedule.",
          type: "error",
        });
        return;
      }

      setIsRescheduling(true);
      try {
        const token = getToken();

        // Helper to format datetime for API (YYYY-MM-DD HH:mm:ss)
        const formatDateTime = (dateTimeString) => {
          if (!dateTimeString) return null;
          // If already in correct format, return as is
          if (
            typeof dateTimeString === "string" &&
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateTimeString)
          ) {
            return dateTimeString;
          }
          // Convert datetime-local format (YYYY-MM-DDTHH:mm) to API format
          const date = new Date(dateTimeString);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        // Helper to extract interviewer IDs
        const getInterviewerIds = (interviewers) => {
          if (!interviewers || !Array.isArray(interviewers)) return [];
          return interviewers
            .map((interviewer) => {
              if (
                typeof interviewer === "string" ||
                typeof interviewer === "number"
              ) {
                return parseInt(interviewer);
              }
              if (typeof interviewer === "object") {
                const id =
                  interviewer.employee_id ||
                  interviewer.id ||
                  interviewer.user_id;
                if (id) {
                  const parsedId = parseInt(String(id));
                  return isNaN(parsedId) ? null : parsedId;
                }
              }
              return null;
            })
            .filter((id) => id !== null);
        };

        const payload = {
          assigned_to: interviewData.assigned_to
            ? parseInt(interviewData.assigned_to)
            : null,
          scheduled_at: formatDateTime(interviewData.scheduled_at),
          interview_type: interviewData.interview_type,
          interview_link: interviewData.interview_link || null,
          location: interviewData.location || null,
          interviewers: getInterviewerIds(interviewData.interviewers),
        };

        await axios.put(
          `https://hrmspayroll.prepstripe.com/api/interview-rounds/${rescheduleInterview.round_id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        setNotification({
          message: "Round rescheduled successfully.",
          type: "success",
        });

        // Refresh the interviews list
        await fetchInterviews(currentPage);
        setIsRescheduleModalOpen(false);
        setRescheduleInterview(null);
      } catch (error) {
        // Handle validation errors
        const errorData = error.response?.data;
        let errorMessage =
          errorData?.message || error.message || "Error rescheduling round";

        if (errorData?.errors) {
          // Format validation errors
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, " ").replace(/\./g, " ");
              return `${fieldName}: ${Array.isArray(messages) ? messages.join(", ") : messages}`;
            })
            .join("; ");
          errorMessage = errorMessages || errorMessage;
        }

        setNotification({
          message: errorMessage,
          type: "error",
        });
      } finally {
        setIsRescheduling(false);
      }
    },
    [rescheduleInterview, fetchInterviews, currentPage],
  );

  // Function to handle schedule next round - open modal
  const handleScheduleNextRound = useCallback((interview) => {
    if (!interview) return;

    const currentRound =
      interview.current_round ?? interview.interview?.current_round;
    const totalRounds =
      interview.total_rounds ?? interview.interview?.total_rounds;
    const roundTypeMapping =
      interview.campaign?.round_type_mapping ||
      interview.round_type_mapping ||
      {};

    // Check if there's a next round
    const nextRoundNumber = currentRound ? currentRound + 1 : 1;
    if (totalRounds && nextRoundNumber > totalRounds) {
      setNotification({
        message: "All rounds have been completed.",
        type: "error",
      });
      return;
    }

    // Get next round type from mapping
    const nextRoundType = roundTypeMapping[String(nextRoundNumber)] || "";

    // Format interview data for the form
    const formattedData = {
      ...interview,
      // Pre-fill with next round data
      scheduled_at: "",
      interview_type: "",
      interview_link: "",
      location: "",
      round_type: nextRoundType,
      interviewers: [],
      assigned_to: "",
      candidate_id: interview.candidate?.id || interview.candidate_id,
      campaign_id: interview.campaign_id || interview.candidate?.campaign_id,
      next_round_number: nextRoundNumber,
    };

    setScheduleNextRoundInterview(formattedData);
    setIsScheduleNextRoundModalOpen(true);
  }, []);

  // Function to handle schedule next round form submit
  const handleScheduleNextRoundSubmit = useCallback(
    async (interviewData) => {
      if (!scheduleNextRoundInterview?.id) {
        setNotification({
          message: "Interview ID not found. Cannot schedule next round.",
          type: "error",
        });
        return;
      }

      setIsSchedulingNextRound(true);
      try {
        const token = getToken();

        // Helper to format datetime for API (YYYY-MM-DD HH:mm:ss)
        const formatDateTime = (dateTimeString) => {
          if (!dateTimeString) return null;
          // If already in correct format, return as is
          if (
            typeof dateTimeString === "string" &&
            /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateTimeString)
          ) {
            return dateTimeString;
          }
          // Convert datetime-local format (YYYY-MM-DDTHH:mm) to API format
          const date = new Date(dateTimeString);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        // Helper to extract interviewer IDs
        const getInterviewerIds = (interviewers) => {
          if (!interviewers || !Array.isArray(interviewers)) return [];
          return interviewers
            .map((interviewer) => {
              if (
                typeof interviewer === "string" ||
                typeof interviewer === "number"
              ) {
                return parseInt(interviewer);
              }
              if (typeof interviewer === "object") {
                const id =
                  interviewer.employee_id ||
                  interviewer.id ||
                  interviewer.user_id;
                if (id) {
                  const parsedId = parseInt(String(id));
                  return isNaN(parsedId) ? null : parsedId;
                }
              }
              return null;
            })
            .filter((id) => id !== null);
        };

        // Use round_number from interviewData if available (from form), otherwise calculate
        const nextRoundNumber =
          interviewData.round_number ||
          scheduleNextRoundInterview.next_round_number ||
          (scheduleNextRoundInterview.current_round || 0) + 1;

        const payload = {
          round_number: nextRoundNumber,
          assigned_to: interviewData.assigned_to
            ? parseInt(interviewData.assigned_to)
            : null,
          interview_type: interviewData.interview_type,
          interview_link: interviewData.interview_link || null,
          location: interviewData.location || null,
          scheduled_at: formatDateTime(interviewData.scheduled_at),
          round_type: interviewData.round_type || "",
          interviewers: getInterviewerIds(interviewData.interviewers),
        };

        await axios.post(
          `https://hrmspayroll.prepstripe.com/api/interviews/${scheduleNextRoundInterview.id}/rounds`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        setNotification({
          message: "Next round scheduled successfully.",
          type: "success",
        });

        // Refresh the interviews list
        await fetchInterviews(currentPage);
        setIsScheduleNextRoundModalOpen(false);
        setScheduleNextRoundInterview(null);
      } catch (error) {
        // Handle validation errors
        const errorData = error.response?.data;
        let errorMessage =
          errorData?.message || error.message || "Error scheduling next round";

        if (errorData?.errors) {
          // Format validation errors
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, " ").replace(/\./g, " ");
              return `${fieldName}: ${Array.isArray(messages) ? messages.join(", ") : messages}`;
            })
            .join("; ");
          errorMessage = errorMessages || errorMessage;
        }

        setNotification({
          message: errorMessage,
          type: "error",
        });
      } finally {
        setIsSchedulingNextRound(false);
      }
    },
    [scheduleNextRoundInterview, fetchInterviews, currentPage],
  );

  // Static interview status options
  const interviewStatusOptions = [
    "Scheduled",
    "Completed",
    "Selected",
    "Rejected",
  ];

  // Define tabs
  const tabs = useMemo(() => {
    const tabList = [{ id: "all", label: "All Interviews" }];

    if (showAssignedTab) {
      tabList.push({ id: "assigned", label: "Interviews Assigned to me" });
    }

    return tabList;
  }, [showAssignedTab]);

  // Use search results if search query, status filter, date filter, or campaign filter exists, otherwise use filtered interviews
  const filteredInterviews = useMemo(() => {
    let filtered = [];

    // If there's a search query, status filter, date filter, or campaign filter, use search results (API handles filtering)
    if (
      debouncedSearchQuery.trim() ||
      selectedInterviewStatusFilter ||
      selectedDateFilter ||
      selectedCampaignFilter
    ) {
      filtered = searchResults;
    } else {
      // Otherwise, use normal filtering on interviews
      filtered = interviews;

      // Filter by campaign (through candidate)
      if (selectedCampaignFilter) {
        filtered = filtered.filter(
          (interview) =>
            String(interview.candidate?.campaign_id) ===
            String(selectedCampaignFilter),
        );
      }
    }

    // Apply tab-based filtering (assigned to me)
    if (activeTab === "assigned" && user?.employee_id) {
      filtered = filtered.filter((interview) => {
        // Check assigned_to field at interview level - it could be a number or string
        const assignedTo =
          interview.assigned_to || interview.interview?.assigned_to;
        if (String(assignedTo) === String(user.employee_id)) {
          return true;
        }

        // Also check if user is assigned in any interview round
        const rounds = interview.rounds || [];
        const isAssignedInRounds = rounds.some((round) => {
          const roundAssignedTo = round.assigned_to;
          return (
            roundAssignedTo &&
            String(roundAssignedTo) === String(user.employee_id)
          );
        });

        // Also check if user is in the interviewers array
        const interviewers = interview.interviewers || [];
        const isInInterviewers = interviewers.some((interviewer) => {
          const interviewerId =
            interviewer.employee_id || interviewer.id || interviewer.user_id;
          return (
            interviewerId && String(interviewerId) === String(user.employee_id)
          );
        });

        // Check interviewers in rounds
        const isInRoundInterviewers = rounds.some((round) => {
          const roundInterviewers = round.interviewers || [];
          return roundInterviewers.some((interviewer) => {
            const interviewerId =
              interviewer.employee_id || interviewer.id || interviewer.user_id;
            return (
              interviewerId &&
              String(interviewerId) === String(user.employee_id)
            );
          });
        });

        return isAssignedInRounds || isInInterviewers || isInRoundInterviewers;
      });
    }

    return filtered;
  }, [
    interviews,
    searchResults,
    debouncedSearchQuery,
    selectedCampaignFilter,
    selectedDateFilter,
    selectedInterviewStatusFilter,
    activeTab,
    user?.employee_id,
  ]);

  // Action Menu Component for dropdown
  const InterviewActionMenu = React.memo(
    ({
      row,
      onEdit,
      onDelete,
      onOfferLetterSend,
      onAcceptOfferLetter,
      onRejectOffer,
      onRescheduleRound,
      onScheduleNextRound,
    }) => {
      const [isOpen, setIsOpen] = useState(false);
      const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
      const [maxHeight, setMaxHeight] = useState(null);
      const dropdownRef = useRef(null);
      const buttonRef = useRef(null);

      const candidateStatus = row?.candidate?.candidate_status;
      const showOfferSend = candidateStatus === "Selected";
      const showAcceptReject = candidateStatus === "Offer Sent";
      const showReject = candidateStatus !== "Rejected";

      // Determine current round status
      const currentRound = row.current_round ?? row.interview?.current_round;
      const rounds = row.rounds || [];
      const matchingRound = rounds.find(
        (round) => round.round_number === currentRound,
      );
      const currentRoundStatus = matchingRound?.status;

      // Get interview status
      const interviewStatus = row?.interview_status;

      // Show Reschedule Round when: current round status is "Scheduled" AND interview status is "InProgress"
      const showReschedule =
        currentRoundStatus === "Scheduled" && interviewStatus === "InProgress";

      // Show Schedule Next Round when: current round status is "Completed" AND interview status is "InProgress"
      const showScheduleNext =
        currentRoundStatus === "Completed" && interviewStatus === "InProgress";

      const handleDropdownToggle = (e) => {
        e.stopPropagation();
        if (isOpen) {
          setIsOpen(false);
        } else {
          setIsOpen(true);
          // Calculate position on click - align dropdown exactly below the button
          const button = e.currentTarget;
          const rect = button.getBoundingClientRect();
          const dropdownWidth = 176; // w-44 = 11rem = 176px
          const gap = 4; // Gap between button and dropdown
          const padding = 8; // Padding from screen edges

          // Position dropdown right-aligned with button's right edge
          let x = rect.right - dropdownWidth;

          // Position dropdown exactly at the bottom of the button - no viewport checks
          let y = rect.bottom + gap;

          // Calculate max-height based on available space below for scrolling
          const spaceBelow = window.innerHeight - y - padding;
          let calculatedMaxHeight = Math.min(spaceBelow, 300); // Max 300px or available space

          // If dropdown would go off the right edge, align to button's left
          if (x < padding) {
            x = Math.max(padding, rect.left);
          }

          // If dropdown would go off the left edge, align to left
          if (x < padding) {
            x = padding;
          }

          // Ensure max height is at least enough for one item
          if (calculatedMaxHeight < 40) {
            calculatedMaxHeight = 40;
          }

          setDropdownCoords({ x, y });
          setMaxHeight(calculatedMaxHeight);
        }
      };

      useEffect(() => {
        const handleClickOutside = (event) => {
          if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target) &&
            buttonRef.current &&
            !buttonRef.current.contains(event.target)
          ) {
            setIsOpen(false);
          }
        };
        if (isOpen) {
          document.addEventListener("mousedown", handleClickOutside);
        }
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }, [isOpen]);

      const handleAction = (action) => {
        setIsOpen(false);
        action();
      };

      return (
        <>
          <div className="relative inline-block text-left">
            <button
              ref={buttonRef}
              onClick={handleDropdownToggle}
              className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
              title="Actions"
            >
              <FaEllipsisV className="w-4 h-4" />
            </button>
          </div>

          {isOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                className="fixed w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[9999] overflow-y-auto"
                style={{
                  top: `${dropdownCoords.y}px`,
                  left: `${dropdownCoords.x}px`,
                  maxHeight: maxHeight ? `${maxHeight}px` : "none",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <HasPermission permission="interviews_update">
                  <button
                    onClick={() => handleAction(() => onEdit(row))}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Edit
                  </button>
                </HasPermission>
                {showReschedule && (
                  <button
                    onClick={() => handleAction(() => onRescheduleRound(row))}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Reschedule Round
                  </button>
                )}
                {showScheduleNext && (
                  <button
                    onClick={() => handleAction(() => onScheduleNextRound(row))}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Schedule Next Round
                  </button>
                )}
                {showOfferSend && (
                  <button
                    onClick={() => handleAction(() => onOfferLetterSend(row))}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Offer Letter Send
                  </button>
                )}
                {showAcceptReject && (
                  <button
                    onClick={() => handleAction(() => onAcceptOfferLetter(row))}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Accept Offer Letter
                  </button>
                )}
                {showReject && (
                  <button
                    onClick={() => handleAction(() => onRejectOffer(row))}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Reject
                  </button>
                )}
                <HasPermission permission="interviews_delete">
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <DeleteButton
                      onDelete={() => {
                        setIsOpen(false);
                        onDelete(row.id);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                      delete_icon={<span>Delete</span>}
                    />
                  </div>
                </HasPermission>
              </div>,
              document.body,
            )}
        </>
      );
    },
  );

  // Drawer Action Buttons Component - Fixed position at bottom right
  const DrawerActionButtons = React.memo(
    ({
      row,
      onEdit,
      onDelete,
      onOfferLetterSend,
      onAcceptOfferLetter,
      onRejectOffer,
      onRescheduleRound,
      onScheduleNextRound,
    }) => {
      const candidateStatus = row?.candidate?.candidate_status;
      const showOfferSend = candidateStatus === "Selected";
      const showAcceptReject = candidateStatus === "Offer Sent";
      const showReject = candidateStatus !== "Rejected";

      // Determine current round status
      const currentRound = row.current_round ?? row.interview?.current_round;
      const rounds = row.rounds || [];
      const matchingRound = rounds.find(
        (round) => round.round_number === currentRound,
      );
      const currentRoundStatus = matchingRound?.status;

      // Get interview status
      const interviewStatus = row?.interview_status;

      // Show Reschedule Round when: current round status is "Scheduled" AND interview status is "InProgress"
      const showReschedule =
        currentRoundStatus === "Scheduled" && interviewStatus === "InProgress";

      // Show Schedule Next Round when: current round status is "Completed" AND interview status is "InProgress"
      const showScheduleNext =
        currentRoundStatus === "Completed" && interviewStatus === "InProgress";

      return (
        <div className="flex flex-row gap-2 items-center flex-wrap justify-end max-w-full">
          <HasPermission permission="interviews_update">
            <button
              onClick={() => onEdit(row)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
            >
              Edit
            </button>
          </HasPermission>
          {showReschedule && (
            <button
              onClick={() => onRescheduleRound(row)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
            >
              Reschedule Round
            </button>
          )}
          {showScheduleNext && (
            <button
              onClick={() => onScheduleNextRound(row)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
            >
              Schedule Next Round
            </button>
          )}
          {showOfferSend && (
            <button
              onClick={() => onOfferLetterSend(row)}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
            >
              Offer Letter Send
            </button>
          )}
          {showAcceptReject && (
            <button
              onClick={() => onAcceptOfferLetter(row)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
            >
              Accept Offer Letter
            </button>
          )}
          {showReject && (
            <button
              onClick={() => onRejectOffer(row)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
            >
              Reject Offer
            </button>
          )}
          <HasPermission permission="interviews_delete">
            <DeleteButton
              onDelete={() => onDelete(row.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors shadow-md text-sm font-medium whitespace-nowrap"
              delete_icon={<span>Delete</span>}
            />
          </HasPermission>
        </div>
      );
    },
  );

  // new code
  const getColumns = useMemo(() => {
    return (currentPage, pageSize) => {
      const baseColumns = [
        {
          label: "Sr. No",
          field: "sr_no",
          align: "center",
          render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
        },
        {
          label: "Candidate Name",
          field: "candidate_id",
          align: "left",
          render: (row) => (
            <button
              onClick={() => openDetailsDrawer(row)}
              className="text-blue-600 hover:underline hover:underline-offset-4"
            >
              {row.candidate?.name || "Unknown Candidate"}
            </button>
          ),
        },
        {
          label: "Total Rounds",
          field: "total_rounds",
          align: "center",
          render: (row) =>
            row.total_rounds ?? row.interview?.total_rounds ?? "-",
        },
        {
          label: "Current Round",
          field: "current_round",
          align: "center",
          render: (row) => {
            const currentRound =
              row.current_round ?? row.interview?.current_round;
            const expectedRoundType =
              row.expected_round_type ?? row.interview?.expected_round_type;

            if (currentRound) {
              if (expectedRoundType) {
                return `${currentRound} (${expectedRoundType})`;
              }
              return currentRound;
            }
            return "-";
          },
        },
        {
          label: "Current Round Status",
          field: "current_round_status",
          align: "center",
          render: (row) => {
            const currentRound =
              row.current_round ?? row.interview?.current_round;
            const rounds = row.rounds || [];

            if (currentRound && rounds.length > 0) {
              const matchingRound = rounds.find(
                (round) => round.round_number === currentRound,
              );

              if (matchingRound?.status) {
                return <StatusBadge status={String(matchingRound.status)} />;
              }
            }
            return <span className="text-gray-400">-</span>;
          },
        },
        {
          label: "Interview Status",
          field: "status",
          align: "center",
          render: (row) => {
            // Based on API response: interview_status at root level
            const interviewStatus = row?.interview_status;

            if (interviewStatus) {
              return <StatusBadge status={String(interviewStatus)} />;
            }
            return <span className="text-gray-400">-</span>;
          },
        },
        {
          label: "Candidate Status",
          field: "candidate_status",
          align: "center",
          render: (row) => {
            const candidateStatus = row?.candidate?.candidate_status;

            if (candidateStatus) {
              return <StatusBadge status={String(candidateStatus)} />;
            }
            return <span className="text-gray-400">-</span>;
          },
        },
      ];

      // Only add Actions column if user is HR
      if (isHR) {
        baseColumns.push({
          label: "Actions",
          field: "actions",
          align: "center",
          render: (row) => (
            <div className="flex items-center justify-center">
              <InterviewActionMenu
                row={row}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onOfferLetterSend={handleOfferLetterSend}
                onAcceptOfferLetter={handleAcceptOfferLetter}
                onRejectOffer={handleRejectOffer}
                onRescheduleRound={handleRescheduleRound}
                onScheduleNextRound={handleScheduleNextRound}
              />
            </div>
          ),
        });
      }

      return baseColumns;
    };
  }, [
    isHR,
    handleEdit,
    handleDelete,
    handleOfferLetterSend,
    handleAcceptOfferLetter,
    handleRejectOffer,
    handleRescheduleRound,
    handleScheduleNextRound,
  ]);

  const handlePageChange = (newPage) => {
    if (
      debouncedSearchQuery.trim() ||
      selectedInterviewStatusFilter ||
      selectedDateFilter ||
      selectedCampaignFilter
    ) {
      // Handle search pagination (when search query, status filter, date filter, or campaign filter is active)
      if (newPage >= 1 && newPage <= searchPagination.totalPages) {
        setSearchPage(newPage);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      // Handle normal pagination
      if (newPage >= 1 && newPage <= pagination.totalPages) {
        setCurrentPage(newPage);
        fetchInterviews(newPage);
      }
    }
  };

  return (
    <div className="flex flex-col gap-y-6 px-1 py-1 font-poppins ">
      {/* Summary Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mx-5 mt-4">
        {loading
          ? summaryCards.map((_, index) => (
              <div
                key={index}
                className="p-5 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4"
              >
                <Skeleton circle width={48} height={48} />
                <div className="flex flex-col gap-2">
                  <Skeleton width={50} height={20} />
                  <Skeleton width={130} height={16} />
                </div>
              </div>
            ))
          : summaryCards.map((card, index) => (
              <div
                key={index}
                className="p-5 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4 transition-all hover:shadow-md"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex-shrink-0">
                  {card.icon}
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white leading-tight">
                    {card.count}
                  </h3>
                  <p
                    className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1 truncate"
                    title={card.label}
                  >
                    {card.label}
                  </p>
                </div>
              </div>
            ))}
      </div>

      <div className="mx-2 bg-white dark:bg-gray-900 pb-28">
        {/* notification compo */}
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10 mx-5">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
            Interviews
          </h2>
          <HasPermission permission="interviews_create">
            <button
              onClick={openModalForAdd}
              className="bg-blue-600 text-white px-4 py-2 font-medium rounded-md hover:bg-blue-700 w-full sm:w-auto"
            >
              Schedule Interview
            </button>
          </HasPermission>
        </div>

        {/* Tabs */}
        {/* {tabs.length > 1 && (
          <div className="border-b dark:border-gray-700 border-gray-200 dark:bg-black mx-5 mb-4">
            <div className="flex px-4 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    // Reset search and filters when switching tabs
                    setSearchQuery("");
                    setDebouncedSearchQuery("");
                    setSelectedInterviewStatusFilter("");
                    setSelectedDateFilter("");
                    setSelectedCampaignFilter("");
                    setSearchResults([]);
                    setSearchPage(1);
                    setCurrentPage(1);
                    fetchInterviews(1);
                  }}
                  className={`flex items-center py-3 px-4 text-sm border-b-2 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600 dark:text-white font-medium"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )} */}

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          {/* Search field with clear button */}
          <div className="relative w-full sm:flex-[2]">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full placeholder:text-xs dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 md:placeholder:text-sm p-2 pr-8 border border-gray-300 rounded-md"
            />
            {(searchQuery ||
              selectedInterviewStatusFilter ||
              selectedDateFilter ||
              selectedCampaignFilter) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setDebouncedSearchQuery("");
                  setSelectedInterviewStatusFilter("");
                  setSelectedDateFilter("");
                  setSelectedCampaignFilter("");
                  setSearchResults([]);
                  setSearchPage(1);
                  // Reset to first page of normal interviews
                  setCurrentPage(1);
                  fetchInterviews(1);
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear search and filters"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Campaign filter */}
          <div className="w-full sm:flex-[2]">
            <select
              value={selectedCampaignFilter}
              onChange={(e) => {
                const newCampaign = e.target.value;
                setSelectedCampaignFilter(newCampaign);
                setSearchPage(1); // Reset to first page when campaign changes
                // If clearing campaign filter and no search query/status/date, reset to normal view
                if (
                  !newCampaign &&
                  !debouncedSearchQuery.trim() &&
                  !selectedInterviewStatusFilter &&
                  !selectedDateFilter
                ) {
                  setSearchResults([]);
                  setCurrentPage(1);
                  fetchInterviews(1);
                }
              }}
              className="w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none text-sm border border-gray-300 rounded-md"
              disabled={loadingCampaignNames}
            >
              <option value="">All Campaigns</option>
              {campaignNames.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.campaign_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date filter */}
          <div className="w-full sm:flex-[1]">
            <input
              type="date"
              value={selectedDateFilter}
              onChange={(e) => {
                const newDate = e.target.value;
                setSelectedDateFilter(newDate);
                setSearchPage(1); // Reset to first page when date changes
                // If clearing date filter and no search query/status/campaign, reset to normal view
                if (
                  !newDate &&
                  !debouncedSearchQuery.trim() &&
                  !selectedInterviewStatusFilter &&
                  !selectedCampaignFilter
                ) {
                  setSearchResults([]);
                  setCurrentPage(1);
                  fetchInterviews(1);
                }
              }}
              className="w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none text-sm border border-gray-300 rounded-md"
            />
          </div>

          {/* Interview Status filter */}
          <div className="w-full sm:flex-[1]">
            <select
              value={selectedInterviewStatusFilter}
              onChange={(e) => {
                const newStatus = e.target.value;
                setSelectedInterviewStatusFilter(newStatus);
                setSearchPage(1); // Reset to first page when status changes
                // If clearing status filter and no search query/date/campaign filter, reset to normal view
                if (
                  !newStatus &&
                  !debouncedSearchQuery.trim() &&
                  !selectedDateFilter &&
                  !selectedCampaignFilter
                ) {
                  setSearchResults([]);
                  setCurrentPage(1);
                  fetchInterviews(1);
                }
              }}
              className="w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none text-sm border border-gray-300 rounded-md"
            >
              <option value="">All Interview Statuses</option>
              {interviewStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* data table */}
        <ReusableTable
          data={filteredInterviews}
          // columns={columns}
          columns={getColumns(
            debouncedSearchQuery.trim() ||
              selectedInterviewStatusFilter ||
              selectedDateFilter ||
              selectedCampaignFilter
              ? searchPage
              : currentPage,
            debouncedSearchQuery.trim() ||
              selectedInterviewStatusFilter ||
              selectedDateFilter ||
              selectedCampaignFilter
              ? searchPagination.perPage
              : pagination.perPage,
          )}
          loading={loading || isSearching}
          filterable={false}
        />

        {filteredInterviews?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={
              (debouncedSearchQuery.trim() ||
              selectedInterviewStatusFilter ||
              selectedDateFilter ||
              selectedCampaignFilter
                ? searchPage
                : currentPage) - 1
            }
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={
              debouncedSearchQuery.trim() ||
              selectedInterviewStatusFilter ||
              selectedDateFilter ||
              selectedCampaignFilter
                ? searchPagination.totalPages
                : pagination.totalPages
            }
            previousLabel="< Prev"
            previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}

        {/* schedule interview modal */}
        <Modal
          title={editingInterview ? "Edit Interview" : "Schedule Interview"}
          isOpen={isModalOpen}
          onRequestClose={closeModal}
        >
          {isModalOpen && (
            <InterviewForm
              onSubmit={handleFormSubmit}
              closeModal={closeModal}
              initialValues={editingInterview || undefined}
              isEditMode={!!editingInterview}
              isScheduling={isScheduling}
            />
          )}
        </Modal>

        <Modal
          title="Proceed to Interview"
          isOpen={isProceedModalOpen}
          onRequestClose={handleCloseProceedModal}
        >
          {proceedCandidate && (
            <InterviewForm
              initialValues={{
                candidate_id: proceedCandidate.id,
                scheduled_at: "",
                status: "Scheduled",
                interview_type: "",
                interview_link: "",
                location: "",
                round_type: "",
              }}
              onSubmit={async (formData) => {
                await updateInterview({ ...editingInterview, ...formData });
                handleCloseProceedModal();
              }}
              closeModal={handleCloseProceedModal}
            />
          )}
        </Modal>

        {/* Drawer */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={closeDetailsDrawer}
          customWidth="w-3/5"
          ChilDiv=""
          ChildParent=""
          showClose={false}
          border_radius="md:rounded-none"
        >
          {selectedInterview ? (
            <>
              <CandidateDetails
                candidate={selectedInterview.candidate}
                interview={selectedInterview}
                onUpdateStatus={handleStatusUpdate}
                onClose={closeDetailsDrawer}
              />
              {/* Action Buttons at Bottom Right */}
              {isHR && (
                <div className="fixed bottom-6 right-6 z-[9999]">
                  <DrawerActionButtons
                    row={selectedInterview}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onOfferLetterSend={handleOfferLetterSend}
                    onAcceptOfferLetter={handleAcceptOfferLetter}
                    onRejectOffer={handleRejectOffer}
                    onRescheduleRound={handleRescheduleRound}
                    onScheduleNextRound={handleScheduleNextRound}
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-500"> </p>
          )}
        </Drawer>

        {/* Offer Letter Send Modal */}
        <Modal
          title="send offer letter"
          isOpen={isOfferLetterSendModalOpen}
          onRequestClose={() => {
            setIsOfferLetterSendModalOpen(false);
            setOfferLetterSendData({ interview: null, file: null });
            setOfferLetterSendErrors({});
          }}
        >
          <form
            onSubmit={handleOfferLetterSendSubmit}
            className="space-y-4 font-poppins"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                Note
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please upload the offer letter document in PDF format (max 5MB).
              </p>
            </div>

            <div>
              <label
                htmlFor="offer_letter_send_file"
                className="block text-sm font-medium text-gray-700 dark:text-white mb-1"
              >
                Upload Document <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="file"
                id="offer_letter_send_file"
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setOfferLetterSendData((prev) => ({ ...prev, file: file }));
                  setOfferLetterSendErrors((prev) => ({ ...prev, file: "" }));
                }}
                disabled={isSubmittingOfferLetterSend}
                className={`w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none border rounded-md ${
                  offerLetterSendErrors.file
                    ? "border-red-500"
                    : "border-gray-300"
                } ${isSubmittingOfferLetterSend ? "opacity-50 cursor-not-allowed" : ""}`}
              />
              {offerLetterSendData.file && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Selected: {offerLetterSendData.file.name} (
                  {(offerLetterSendData.file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              {offerLetterSendErrors.file && (
                <p className="text-red-500 text-sm mt-1">
                  {offerLetterSendErrors.file}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                PDF only, maximum 5MB
              </p>
            </div>

            <div className="flex justify-end gap-2 items-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsOfferLetterSendModalOpen(false);
                  setOfferLetterSendData({
                    interview: null,
                    note: "",
                    file: null,
                  });
                  setOfferLetterSendErrors({});
                }}
                disabled={isSubmittingOfferLetterSend}
                className={`px-12 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md ${
                  isSubmittingOfferLetterSend
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingOfferLetterSend}
                className={`px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                  isSubmittingOfferLetterSend
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isSubmittingOfferLetterSend ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </Modal>

        {/* Accept Offer Letter Modal */}
        <Modal
          title="accept offer"
          isOpen={isAcceptOfferModalOpen}
          onRequestClose={() => {
            setIsAcceptOfferModalOpen(false);
            setAcceptOfferData({ interview: null, file: null });
          }}
        >
          <form
            onSubmit={handleAcceptOfferSubmit}
            className="space-y-4 font-poppins"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                Note
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please upload the signed offer letter document in PDF format
                (max 5MB).
              </p>
            </div>

            <div>
              <label
                htmlFor="accept_offer_file"
                className="block text-sm font-medium text-gray-700 dark:text-white mb-1"
              >
                Upload Signed Document{" "}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="file"
                id="accept_offer_file"
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setAcceptOfferData((prev) => ({ ...prev, file: file }));
                }}
                disabled={isSubmittingAcceptOffer}
                className="w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none border border-gray-300 rounded-md"
              />
              {acceptOfferData.file && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Selected: {acceptOfferData.file.name} (
                  {(acceptOfferData.file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                PDF only, maximum 5MB
              </p>
            </div>

            <div className="flex justify-end gap-2 items-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsAcceptOfferModalOpen(false);
                  setAcceptOfferData({ interview: null, note: "", file: null });
                }}
                disabled={isSubmittingAcceptOffer}
                className={`px-12 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md ${
                  isSubmittingAcceptOffer ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingAcceptOffer}
                className={`px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                  isSubmittingAcceptOffer ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSubmittingAcceptOffer ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </Modal>

        {/* Reschedule Round Modal */}
        <Modal
          title="Reschedule Round"
          isOpen={isRescheduleModalOpen}
          onRequestClose={() => {
            setIsRescheduleModalOpen(false);
            setRescheduleInterview(null);
          }}
        >
          {isRescheduleModalOpen && rescheduleInterview && (
            <InterviewForm
              onSubmit={handleRescheduleSubmit}
              closeModal={() => {
                setIsRescheduleModalOpen(false);
                setRescheduleInterview(null);
              }}
              initialValues={rescheduleInterview}
              mode="reschedule"
              isScheduling={isRescheduling}
            />
          )}
        </Modal>

        {/* Schedule Next Round Modal */}
        <Modal
          title="Schedule Next Round"
          isOpen={isScheduleNextRoundModalOpen}
          onRequestClose={() => {
            setIsScheduleNextRoundModalOpen(false);
            setScheduleNextRoundInterview(null);
          }}
        >
          {isScheduleNextRoundModalOpen && scheduleNextRoundInterview && (
            <InterviewForm
              onSubmit={handleScheduleNextRoundSubmit}
              closeModal={() => {
                setIsScheduleNextRoundModalOpen(false);
                setScheduleNextRoundInterview(null);
              }}
              initialValues={scheduleNextRoundInterview}
              mode="scheduleNext"
              isScheduling={isSchedulingNextRound}
            />
          )}
        </Modal>

        {/* Reject Offer Modal */}
        <Modal
          title="Reject reason"
          isOpen={isRejectModalOpen}
          onRequestClose={() => {
            setIsRejectModalOpen(false);
            setRejectOfferData({ interview: null, reason: "" });
            setRejectOfferErrors({});
          }}
        >
          <form
            onSubmit={handleRejectSubmit}
            className="space-y-4 font-poppins"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                Note
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please provide a reason for rejecting the offer letter below.
              </p>
            </div>

            <div>
              <label
                htmlFor="reject_offer_reason"
                className="block text-sm font-medium text-gray-700 dark:text-white mb-1"
              >
                Reason <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="reject_offer_reason"
                value={rejectOfferData.reason}
                onChange={(e) => {
                  const value = e.target.value;
                  setRejectOfferData((prev) => ({ ...prev, reason: value }));
                  if (value.length > 200) {
                    setRejectOfferErrors((prev) => ({
                      ...prev,
                      reason: "Reason must be 200 characters or less.",
                    }));
                  } else {
                    setRejectOfferErrors((prev) => ({ ...prev, reason: "" }));
                  }
                }}
                disabled={isSubmittingRejectOffer}
                rows={4}
                className={`w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none border rounded-md ${
                  rejectOfferErrors.reason
                    ? "border-red-500"
                    : "border-gray-300"
                } ${isSubmittingRejectOffer ? "opacity-50 cursor-not-allowed" : ""}`}
                placeholder="Enter rejection reason (required, max 200 characters)"
                required
              />
              <div className="flex justify-between items-center mt-1">
                <div>
                  {rejectOfferErrors.reason && (
                    <p className="text-red-500 text-sm">
                      {rejectOfferErrors.reason}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Minimum 10 characters, maximum 200 characters
                  </p>
                </div>
                <p
                  className={`text-xs ${
                    rejectOfferData.reason.length > 200
                      ? "text-red-500"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {rejectOfferData.reason.length}/200
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 items-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsRejectModalOpen(false);
                  setRejectOfferData({ interview: null, reason: "" });
                  setRejectOfferErrors({});
                }}
                disabled={isSubmittingRejectOffer}
                className={`px-12 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md ${
                  isSubmittingRejectOffer ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isSubmittingRejectOffer || rejectOfferData.reason.length > 200
                }
                className={`px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                  isSubmittingRejectOffer || rejectOfferData.reason.length > 200
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isSubmittingRejectOffer ? "Submitting..." : "Reject"}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default React.memo(Interviews);
